import { useCallback } from 'react';

import {
  generateFeedbackTraceId,
  trackFeedback,
  trackFeedbackWithAutoTraceId,
} from '../langfuse';

export interface FeedbackData {
  rating: 'positive' | 'negative';
  selectedOptions: string[];
  additionalComments?: string;
  metadata?: Record<string, any>;
}

export interface UseLangfuseFeedbackOptions {
  context: string;
  estimatedVisitId?: string;
  unifiedAppointmentId?: string;
  backendTraceId?: string; // Optional backend trace ID for linking
}

/**
 * Hook for tracking user feedback to Langfuse
 */
export const useLangfuseFeedback = ({
  context,
  estimatedVisitId,
  unifiedAppointmentId,
  backendTraceId,
}: UseLangfuseFeedbackOptions) => {
  const trackFeedbackWithTraceId = useCallback(
    async (traceId: string, feedbackData: FeedbackData) => {
      return trackFeedback(
        traceId,
        feedbackData.rating,
        feedbackData.selectedOptions,
        feedbackData.additionalComments,
        context,
        feedbackData.metadata,
      );
    },
    [context],
  );

  const trackFeedbackAutoTrace = useCallback(
    async (feedbackData: FeedbackData) => {
      if (!estimatedVisitId || !unifiedAppointmentId) {
        console.warn(`Missing required IDs for ${context} feedback tracking`);
        return;
      }

      // If we have a backend trace ID, use it directly for linking
      if (backendTraceId) {
        return trackFeedback(
          backendTraceId,
          feedbackData.rating,
          feedbackData.selectedOptions,
          feedbackData.additionalComments,
          context,
          {
            ...feedbackData.metadata,
            linkedToBackendTrace: true,
            frontendTraceId: generateFeedbackTraceId(
              context,
              estimatedVisitId,
              unifiedAppointmentId,
            ),
          },
        );
      }

      // Fallback to auto-generated trace ID
      return trackFeedbackWithAutoTraceId(
        context,
        feedbackData.rating,
        feedbackData.selectedOptions,
        feedbackData.additionalComments,
        estimatedVisitId,
        unifiedAppointmentId,
        feedbackData.metadata,
      );
    },
    [context, estimatedVisitId, unifiedAppointmentId, backendTraceId],
  );

  return {
    trackFeedbackWithTraceId,
    trackFeedbackAutoTrace,
  };
};
